import React, { useState } from 'react';
import { Box, Grid, Paper, Typography, Slider, Button, Select, MenuItem, FormControl, InputLabel, Container, Chip, Stack, Tooltip, Switch, FormControlLabel, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { ChromePicker, ColorResult } from 'react-color';
import { ContentCopy, Favorite, Shuffle, AutoAwesome, Download, ExpandMore, Code, Animation } from '@mui/icons-material';

// Popüler preset gradyanlar
const presetGradients = [
  { name: 'Sunset', color1: '#FF512F', color2: '#F09819', angle: 135 },
  { name: 'Ocean', color1: '#2193b0', color2: '#6dd5ed', angle: 90 },
  { name: 'Purple Dream', color1: '#8E2DE2', color2: '#4A00E0', angle: 45 },
  { name: 'Mango', color1: '#ffe259', color2: '#ffa751', angle: 180 },
  { name: 'Instagram', color1: '#833ab4', color2: '#fd1d1d', angle: 45 },
  { name: 'Cool Blues', color1: '#2193b0', color2: '#6dd5ed', angle: 0 },
  { name: 'Emerald', color1: '#348F50', color2: '#56B4D3', angle: 120 },
  { name: 'Cherry', color1: '#EB3349', color2: '#F45C43', angle: 90 },
  { name: 'Midnight', color1: '#232526', color2: '#414345', angle: 180 },
  { name: 'Cosmic', color1: '#ff00cc', color2: '#333399', angle: 135 },
  { name: 'Lime', color1: '#a8e063', color2: '#56ab2f', angle: 90 },
  { name: 'Rose', color1: '#ff9a9e', color2: '#fecfef', angle: 45 },
];

const gradientLanguages = [
  { name: 'XAML Background', color: '#FF9800' },
  { name: 'XAML Foreground', color: '#88CB70' },
  { name: 'React (sx)', color: '#61dafb' },
  { name: 'React (inline style)', color: '#FFD600' },
  { name: 'XAML', color: '#FF9800' },
  { name: 'CSS', color: '#9C27B0' },
  { name: 'C#', color: '#607D8B' },
  { name: 'HTML', color: '#E44D26' },
  { name: 'Python', color: '#3776AB' },
  { name: 'VB.NET', color: '#00599C' },
  { name: 'Excel VBA', color: '#217346' },
  { name: '.NET MAUI', color: '#512BD4' },
  { name: 'Flutter', color: '#02569B' },
  { name: 'Java', color: '#b07219' },
  { name: 'Kotlin', color: '#A97BFF' },
  { name: 'Swift', color: '#F05138' },
  { name: 'Objective-C', color: '#438EFF' },
  { name: 'Android XML', color: '#3DDC84' },
  { name: 'SCSS', color: '#C6538C' },
  { name: 'LESS', color: '#1D365D' },
  { name: 'Tailwind CSS', color: '#38BDF8' },
  { name: 'QML', color: '#41CD52' },
  { name: 'Unity C#', color: '#222C37' },
  { name: 'MATLAB', color: '#E16737' },
  { name: 'Figma', color: '#A259FF' },
  { name: 'SVG', color: '#FFB13B' },
  { name: 'Dart', color: '#00B4AB' },
  { name: 'PowerShell', color: '#012456' },
  { name: 'Rust', color: '#DEA584' },
  { name: 'Go', color: '#00ADD8' },
  { name: 'C++ (Qt)', color: '#005F87' },
  { name: 'Delphi', color: '#E37933' },
  { name: 'PHP', color: '#777BB4' },
  { name: 'Laravel', color: '#FF2D20' },
];

function getGradientCodesByLang(lang: string, angle: number, color1: string, color2: string) {
  // Her dil için linear ve radial kodları döndür
  switch (lang) {
    case 'XAML Background':
      return [
        { type: 'Linear', code: `Background=\"LinearGradientBrush(Angle=${angle}, Colors=[${color1.toUpperCase()}, ${color2.toUpperCase()}])\"` },
        { type: 'Radial', code: `Background=\"RadialGradientBrush(Colors=[${color1.toUpperCase()}, ${color2.toUpperCase()}])\"` },
      ];
    case 'XAML Foreground':
      return [
        { type: 'Linear', code: `Foreground=\"LinearGradientBrush(Angle=${angle}, Colors=[${color1.toUpperCase()}, ${color2.toUpperCase()}])\"` },
        { type: 'Radial', code: `Foreground=\"RadialGradientBrush(Colors=[${color1.toUpperCase()}, ${color2.toUpperCase()}])\"` },
      ];
    case 'React (sx)':
      return [
        { type: 'Linear', code: `sx={{ background: 'linear-gradient(${angle}deg, ${color1}, ${color2})' }}` },
        { type: 'Radial', code: `sx={{ background: 'radial-gradient(circle, ${color1}, ${color2})' }}` },
      ];
    case 'React (inline style)':
      return [
        { type: 'Linear', code: `style={{ background: 'linear-gradient(${angle}deg, ${color1}, ${color2})' }}` },
        { type: 'Radial', code: `style={{ background: 'radial-gradient(circle, ${color1}, ${color2})' }}` },
      ];
    case 'XAML':
      return [
        { type: 'Linear', code: `<LinearGradientBrush StartPoint=\"0,0\" EndPoint=\"1,0\" Angle=\"${angle}\">\n  <GradientStop Color=\"${color1.toUpperCase()}\" Offset=\"0\" />\n  <GradientStop Color=\"${color2.toUpperCase()}\" Offset=\"1\" />\n</LinearGradientBrush>` },
        { type: 'Radial', code: `<RadialGradientBrush>\n  <GradientStop Color=\"${color1.toUpperCase()}\" Offset=\"0\" />\n  <GradientStop Color=\"${color2.toUpperCase()}\" Offset=\"1\" />\n</RadialGradientBrush>` },
      ];
    case 'CSS':
      return [
        { type: 'Linear', code: `background: linear-gradient(${angle}deg, ${color1}, ${color2});` },
        { type: 'Radial', code: `background: radial-gradient(circle, ${color1}, ${color2});` },
      ];
    case 'C#':
      return [
        { type: 'Linear', code: `new LinearGradientBrush(new Point(0,0), new Point(1,0), ColorTranslator.FromHtml(\"${color1}\"), ColorTranslator.FromHtml(\"${color2}\"));` },
        { type: 'Radial', code: `new PathGradientBrush(new Point[] { center, edge1, edge2 }) { CenterColor = ColorTranslator.FromHtml(\"${color1}\"), SurroundColors = new[] { ColorTranslator.FromHtml(\"${color2}\") } }` },
      ];
    case 'HTML':
      return [
        { type: 'Linear', code: `<div style=\"background: linear-gradient(${angle}deg, ${color1}, ${color2});\"></div>` },
        { type: 'Radial', code: `<div style=\"background: radial-gradient(circle, ${color1}, ${color2});\"></div>` },
      ];
    case 'Python':
      return [
        { type: 'Linear', code: `gradient = f"linear-gradient(${angle}deg, ${color1}, ${color2})"` },
        { type: 'Radial', code: `gradient = f"radial-gradient(circle, ${color1}, ${color2})"` },
      ];
    case 'VB.NET':
      return [
        { type: 'Linear', code: `Dim brush As New LinearGradientBrush(Point1, Point2, ColorTranslator.FromHtml(\"${color1}\"), ColorTranslator.FromHtml(\"${color2}\"))` },
        { type: 'Radial', code: `Dim brush As New PathGradientBrush(points) : brush.CenterColor = ColorTranslator.FromHtml(\"${color1}\") : brush.SurroundColors = New Color() {ColorTranslator.FromHtml(\"${color2}\")}` },
      ];
    case 'Excel VBA':
      return [
        { type: 'Linear', code: `Range.Interior.Pattern = xlPatternLinearGradient\nRange.Interior.Gradient.ColorStops(1).Color = RGB(${parseInt(color1.slice(1, 3), 16)},${parseInt(color1.slice(3, 5), 16)},${parseInt(color1.slice(5, 7), 16)})\nRange.Interior.Gradient.ColorStops(2).Color = RGB(${parseInt(color2.slice(1, 3), 16)},${parseInt(color2.slice(3, 5), 16)},${parseInt(color2.slice(5, 7), 16)})` },
        { type: 'Radial', code: `// Excel VBA için radial gradient örneği eklenebilir` },
      ];
    case '.NET MAUI':
      return [
        { type: 'Linear', code: `<LinearGradientBrush StartPoint=\"0,0\" EndPoint=\"1,0\">\n  <GradientStop Color=\"${color1.toUpperCase()}\" Offset=\"0\" />\n  <GradientStop Color=\"${color2.toUpperCase()}\" Offset=\"1\" />\n</LinearGradientBrush>` },
        { type: 'Radial', code: `<RadialGradientBrush>\n  <GradientStop Color=\"${color1.toUpperCase()}\" Offset=\"0\" />\n  <GradientStop Color=\"${color2.toUpperCase()}\" Offset=\"1\" />\n</RadialGradientBrush>` },
      ];
    case 'Flutter':
      return [
        { type: 'Linear', code: `Container(\n  decoration: BoxDecoration(\n    gradient: LinearGradient(\n      colors: [Color(0xFF${color1.slice(1).toUpperCase()}), Color(0xFF${color2.slice(1).toUpperCase()})],\n      begin: Alignment.topLeft,\n      end: Alignment.bottomRight,\n      transform: GradientRotation(${angle} * 3.14 / 180),\n    ),\n  ),\n)` },
        { type: 'Radial', code: `Container(\n  decoration: BoxDecoration(\n    gradient: RadialGradient(\n      colors: [Color(0xFF${color1.slice(1).toUpperCase()}), Color(0xFF${color2.slice(1).toUpperCase()})],\n    ),\n  ),\n)` },
      ];
    case 'Java':
      return [
        { type: 'Linear', code: `LinearGradientPaint paint = new LinearGradientPaint(0, 0, 100, 0, new float[]{0f, 1f}, new Color[]{Color.decode(\"${color1}\"), Color.decode(\"${color2}\")});` },
        { type: 'Radial', code: `RadialGradientPaint paint = new RadialGradientPaint(new Point2D.Float(50, 50), 50, new float[]{0f, 1f}, new Color[]{Color.decode(\"${color1}\"), Color.decode(\"${color2}\")});` },
      ];
    case 'Kotlin':
      return [
        { type: 'Linear', code: `val paint = LinearGradient(0f, 0f, 100f, 0f, Color.parseColor(\"${color1}\"), Color.parseColor(\"${color2}\"), Shader.TileMode.CLAMP)` },
        { type: 'Radial', code: `val paint = RadialGradient(50f, 50f, 50f, Color.parseColor(\"${color1}\"), Color.parseColor(\"${color2}\"), Shader.TileMode.CLAMP)` },
      ];
    case 'Swift':
      return [
        { type: 'Linear', code: `let gradient = CAGradientLayer()\ngradient.colors = [UIColor(hex: \"${color1}\").cgColor, UIColor(hex: \"${color2}\").cgColor]\ngradient.startPoint = CGPoint(x: 0, y: 0)\ngradient.endPoint = CGPoint(x: 1, y: 0)` },
        { type: 'Radial', code: `let gradient = RadialGradient(gradient: Gradient(colors: [Color(hex: \"${color1}\"), Color(hex: \"${color2}\")]), center: .center, startRadius: 0, endRadius: 100)` },
      ];
    case 'Objective-C':
      return [
        { type: 'Linear', code: `CAGradientLayer *gradient = [CAGradientLayer layer];\ngradient.colors = @[(__bridge id)[UIColor colorWithHexString:@\"${color1}\"].CGColor, (__bridge id)[UIColor colorWithHexString:@\"${color2}\"].CGColor];\ngradient.startPoint = CGPointMake(0, 0);\ngradient.endPoint = CGPointMake(1, 0);` },
        { type: 'Radial', code: `// iOS için Core Graphics ile radial gradient örneği` },
      ];
    case 'Android XML':
      return [
        { type: 'Linear', code: `<gradient android:type=\"linear\" android:angle=\"${angle}\" android:startColor=\"${color1}\" android:endColor=\"${color2}\" />` },
        { type: 'Radial', code: `<gradient android:type=\"radial\" android:centerColor=\"${color1}\" android:endColor=\"${color2}\" android:gradientRadius=\"50\" />` },
      ];
    case 'SCSS':
      return [
        { type: 'Linear', code: `$gradient: linear-gradient(${angle}deg, ${color1}, ${color2});` },
        { type: 'Radial', code: `$gradient: radial-gradient(circle, ${color1}, ${color2});` },
      ];
    case 'LESS':
      return [
        { type: 'Linear', code: `@gradient: linear-gradient(${angle}deg, ${color1}, ${color2});` },
        { type: 'Radial', code: `@gradient: radial-gradient(circle, ${color1}, ${color2});` },
      ];
    case 'Tailwind CSS':
      return [
        { type: 'Linear', code: `bg-gradient-to-r from-[${color1}] to-[${color2}]` },
        { type: 'Radial', code: `bg-[radial-gradient(circle,${color1},${color2})]` },
      ];
    case 'QML':
      return [
        { type: 'Linear', code: `gradient: Gradient { GradientStop { position: 0.0; color: \"${color1}\" } GradientStop { position: 1.0; color: \"${color2}\" } }` },
        { type: 'Radial', code: `gradient: RadialGradient { GradientStop { position: 0.0; color: \"${color1}\" } GradientStop { position: 1.0; color: \"${color2}\" } }` },
      ];
    case 'Unity C#':
      return [
        { type: 'Linear', code: `// Unity'de gradient için özel shader veya Gradient API kullanılabilir` },
        { type: 'Radial', code: `// Unity'de gradient için özel shader veya Gradient API kullanılabilir` },
      ];
    case 'MATLAB':
      return [
        { type: 'Linear', code: `% MATLAB'da gradient için imshow veya patch kullanılabilir` },
        { type: 'Radial', code: `% MATLAB'da gradient için imshow veya patch kullanılabilir` },
      ];
    case 'Figma':
      return [
        { type: 'Linear', code: `Linear: ${color1.toUpperCase()} → ${color2.toUpperCase()}` },
        { type: 'Radial', code: `Radial: ${color1.toUpperCase()} → ${color2.toUpperCase()}` },
      ];
    case 'SVG':
      return [
        { type: 'Linear', code: `<linearGradient id=\"grad1\" x1=\"0%\" y1=\"0%\" x2=\"100%\" y2=\"0%\">\n  <stop offset=\"0%\" style=\"stop-color:${color1};stop-opacity:1\" />\n  <stop offset=\"100%\" style=\"stop-color:${color2};stop-opacity:1\" />\n</linearGradient>` },
        { type: 'Radial', code: `<radialGradient id=\"grad2\" cx=\"50%\" cy=\"50%\" r=\"50%\">\n  <stop offset=\"0%\" style=\"stop-color:${color1};stop-opacity:1\" />\n  <stop offset=\"100%\" style=\"stop-color:${color2};stop-opacity:1\" />\n</radialGradient>` },
      ];
    case 'Dart':
      return [
        { type: 'Linear', code: `LinearGradient(colors: [Color(0xFF${color1.slice(1).toUpperCase()}), Color(0xFF${color2.slice(1).toUpperCase()})])` },
        { type: 'Radial', code: `RadialGradient(colors: [Color(0xFF${color1.slice(1).toUpperCase()}), Color(0xFF${color2.slice(1).toUpperCase()})])` },
      ];
    case 'PowerShell':
      return [
        { type: 'Linear', code: `# PowerShell'de doğrudan gradient yoktur` },
        { type: 'Radial', code: `# PowerShell'de doğrudan gradient yoktur` },
      ];
    case 'Rust':
      return [
        { type: 'Linear', code: `// Rust'ta doğrudan gradient yoktur` },
        { type: 'Radial', code: `// Rust'ta doğrudan gradient yoktur` },
      ];
    case 'Go':
      return [
        { type: 'Linear', code: `// Go'da doğrudan gradient yoktur` },
        { type: 'Radial', code: `// Go'da doğrudan gradient yoktur` },
      ];
    case 'C++ (Qt)':
      return [
        { type: 'Linear', code: `QLinearGradient gradient(0, 0, 100, 0);\ngradient.setColorAt(0, QColor(\"${color1}\"));\ngradient.setColorAt(1, QColor(\"${color2}\"));` },
        { type: 'Radial', code: `QRadialGradient gradient(50, 50, 50);\ngradient.setColorAt(0, QColor(\"${color1}\"));\ngradient.setColorAt(1, QColor(\"${color2}\"));` },
      ];
    case 'Delphi':
      return [
        { type: 'Linear', code: `// Delphi'de gradient için TGradient veya canvas ile yapılabilir` },
        { type: 'Radial', code: `// Delphi'de gradient için TGradient veya canvas ile yapılabilir` },
      ];
    case 'PHP':
      return [
        { type: 'Linear', code: `$gradient = "linear-gradient(${angle}deg, ${color1}, ${color2})";` },
        { type: 'Radial', code: `$gradient = "radial-gradient(circle, ${color1}, ${color2})";` },
      ];
    case 'Laravel':
      return [
        { type: 'Linear', code: `style=\"background: linear-gradient(${angle}deg, ${color1}, ${color2});\"` },
        { type: 'Radial', code: `style=\"background: radial-gradient(circle, ${color1}, ${color2});\"` },
      ];
    default:
      return [];
  }
}

function getRandomColor() {
  return '#' + Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0').toUpperCase();
}

const FAVORITES_KEY = 'favorites_v1';

function getGradientCSS(type: string, angle: number, color1: string, color2: string) {
  if (type === 'linear') {
    return `linear-gradient(${angle}deg, ${color1}, ${color2})`;
  } else {
    return `radial-gradient(circle, ${color1}, ${color2})`;
  }
}

const GradientCreator: React.FC = () => {
  const [color1, setColor1] = useState('#ff0000');
  const [color2, setColor2] = useState('#0000ff');
  const [angle, setAngle] = useState(0);
  const [type, setType] = useState('linear');
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [selectedLang, setSelectedLang] = useState('CSS');
  const [favAdded, setFavAdded] = useState(false);
  const [animated, setAnimated] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  const handleColor1Change = (color: ColorResult) => {
    setColor1(color.hex);
  };

  const handleColor2Change = (color: ColorResult) => {
    setColor2(color.hex);
  };

  const handleAngleChange = (_event: Event, newValue: number | number[]) => {
    setAngle(newValue as number);
  };

  const handleTypeChange = (event: any) => {
    setType(event.target.value);
  };

  const handleLangChange = (event: any) => {
    setSelectedLang(event.target.value);
  };

  const handleCopy = (code: string, idx: number) => {
    navigator.clipboard.writeText(code);
    setCopiedIndex(idx);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const handleCopyText = (text: string, which: string) => {
    navigator.clipboard.writeText(text);
    setCopied(which);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleAddFavorite = () => {
    const fav = {
      id: 'gradient-' + [color1, color2, angle, type].join('-'),
      type: 'gradient',
      name: `Gradient (${type.charAt(0).toUpperCase() + type.slice(1)})`,
      data: { colors: [color1, color2], angle, gradientType: type, css: getGradientCSS(type, angle, color1, color2) },
      date: new Date().toISOString(),
    };
    let favs: any[] = [];
    try {
      favs = JSON.parse(localStorage.getItem(FAVORITES_KEY) || '[]');
    } catch { }
    if (!favs.some((f: any) => f.id === fav.id)) {
      favs.unshift(fav);
      localStorage.setItem(FAVORITES_KEY, JSON.stringify(favs));
      window.dispatchEvent(new Event('favoriteAdded'));
      setFavAdded(true);
      setTimeout(() => setFavAdded(false), 1500);
    }
  };

  // SVG Dışa Aktarma
  const getSVGCode = () => {
    if (type === 'linear') {
      const angleRad = (angle * Math.PI) / 180;
      const x2 = Math.round(50 + 50 * Math.cos(angleRad));
      const y2 = Math.round(50 + 50 * Math.sin(angleRad));
      return `<svg width="200" height="200" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <linearGradient id="grad" x1="0%" y1="0%" x2="${x2}%" y2="${y2}%">
      <stop offset="0%" style="stop-color:${color1};stop-opacity:1" />
      <stop offset="100%" style="stop-color:${color2};stop-opacity:1" />
    </linearGradient>
  </defs>
  <rect width="200" height="200" fill="url(#grad)" />
</svg>`;
    } else {
      return `<svg width="200" height="200" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <radialGradient id="grad" cx="50%" cy="50%" r="50%">
      <stop offset="0%" style="stop-color:${color1};stop-opacity:1" />
      <stop offset="100%" style="stop-color:${color2};stop-opacity:1" />
    </radialGradient>
  </defs>
  <rect width="200" height="200" fill="url(#grad)" />
</svg>`;
    }
  };

  // Canvas Kodu
  const getCanvasCode = () => {
    if (type === 'linear') {
      return `const canvas = document.getElementById('canvas');
const ctx = canvas.getContext('2d');
const gradient = ctx.createLinearGradient(0, 0, canvas.width, 0);
gradient.addColorStop(0, '${color1}');
gradient.addColorStop(1, '${color2}');
ctx.fillStyle = gradient;
ctx.fillRect(0, 0, canvas.width, canvas.height);`;
    } else {
      return `const canvas = document.getElementById('canvas');
const ctx = canvas.getContext('2d');
const centerX = canvas.width / 2;
const centerY = canvas.height / 2;
const gradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, centerX);
gradient.addColorStop(0, '${color1}');
gradient.addColorStop(1, '${color2}');
ctx.fillStyle = gradient;
ctx.fillRect(0, 0, canvas.width, canvas.height);`;
    }
  };

  // Animasyonlu CSS
  const getAnimatedCSS = () => {
    return `@keyframes gradientAnimation {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}

.animated-gradient {
  background: linear-gradient(-45deg, ${color1}, ${color2}, ${color1}, ${color2});
  background-size: 400% 400%;
  animation: gradientAnimation 3s ease infinite;
}`;
  };

  // SVG Dosyası İndir
  const downloadSVG = () => {
    const svgCode = getSVGCode();
    const blob = new Blob([svgCode], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'gradient.svg';
    a.click();
    URL.revokeObjectURL(url);
  };

  const codes = getGradientCodesByLang(selectedLang, angle, color1, color2);
  const langColor = gradientLanguages.find(l => l.name === selectedLang)?.color || '#2196F3';

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" gutterBottom>
          Gradient Oluşturucu
        </Typography>
        <Button
          variant="contained"
          sx={{ mb: 2, mr: 1, bgcolor: "#3733b6" }}
          onClick={() => {
            setColor1(getRandomColor());
            setColor2(getRandomColor());
            setAngle(Math.floor(Math.random() * 361));
            setType(Math.random() > 0.5 ? 'linear' : 'radial');
          }}
        >
          Rastgele Gradient Üret
        </Button>
        <Button
          variant="contained"
          color={favAdded ? 'success' : 'secondary'}
          startIcon={<Favorite />}
          onClick={handleAddFavorite}
          sx={{ mb: 2 }}
          disabled={favAdded}
        >
          {favAdded ? 'Favorilere Eklendi' : 'Favorilere Ekle'}
        </Button>

        {/* Preset Gradyanlar */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AutoAwesome /> Hazır Gradyanlar
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {presetGradients.map((preset) => (
              <Tooltip key={preset.name} title={preset.name}>
                <Box
                  onClick={() => {
                    setColor1(preset.color1);
                    setColor2(preset.color2);
                    setAngle(preset.angle);
                    setType('linear');
                  }}
                  sx={{
                    width: 60,
                    height: 40,
                    borderRadius: 1,
                    cursor: 'pointer',
                    background: `linear-gradient(${preset.angle}deg, ${preset.color1}, ${preset.color2})`,
                    border: '2px solid transparent',
                    '&:hover': {
                      border: '2px solid #fff',
                      transform: 'scale(1.1)',
                      boxShadow: 3,
                    },
                    transition: 'all 0.2s',
                  }}
                />
              </Tooltip>
            ))}
          </Stack>
        </Paper>

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Renk 1
              </Typography>
              <ChromePicker
                color={color1}
                onChange={handleColor1Change}
              />
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Renk 2
              </Typography>
              <ChromePicker
                color={color2}
                onChange={handleColor2Change}
              />
            </Paper>
            <Paper sx={{ p: 2, mt: 2, textAlign: 'center' }}>
              {/* Animasyonlu veya Statik Önizleme */}
              <Box
                sx={{
                  width: '100%',
                  height: 120,
                  background: type === 'linear'
                    ? `linear-gradient(${angle}deg, ${color1}, ${color2})`
                    : `radial-gradient(circle, ${color1}, ${color2})`,
                  borderRadius: 1,
                  mb: 2,
                  ...(animated && {
                    background: `linear-gradient(-45deg, ${color1}, ${color2}, ${color1}, ${color2})`,
                    backgroundSize: '400% 400%',
                    animation: 'gradientAnim 3s ease infinite',
                    '@keyframes gradientAnim': {
                      '0%': { backgroundPosition: '0% 50%' },
                      '50%': { backgroundPosition: '100% 50%' },
                      '100%': { backgroundPosition: '0% 50%' },
                    },
                  }),
                }}
              />

              {/* Animasyon Toggle */}
              <FormControlLabel
                control={<Switch checked={animated} onChange={(e) => setAnimated(e.target.checked)} />}
                label={<Stack direction="row" alignItems="center" gap={0.5}><Animation /> Animasyonlu</Stack>}
                sx={{ mb: 2 }}
              />

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Gradient Türü</InputLabel>
                <Select
                  value={type}
                  label="Gradient Türü"
                  onChange={handleTypeChange}
                >
                  <MenuItem value="linear">Doğrusal</MenuItem>
                  <MenuItem value="radial">Dairesel</MenuItem>
                </Select>
              </FormControl>

              {/* Görsel Açı Seçici */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    border: '2px solid #ccc',
                    position: 'relative',
                    cursor: 'pointer',
                    background: `conic-gradient(from ${angle}deg, ${color1}, ${color2}, ${color1})`,
                  }}
                  onClick={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const x = e.clientX - rect.left - 30;
                    const y = e.clientY - rect.top - 30;
                    const newAngle = Math.round((Math.atan2(y, x) * 180 / Math.PI + 90 + 360) % 360);
                    setAngle(newAngle);
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      width: 4,
                      height: 25,
                      bgcolor: '#fff',
                      borderRadius: 2,
                      transformOrigin: 'center top',
                      transform: `translate(-50%, 0) rotate(${angle}deg)`,
                      boxShadow: 1,
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography gutterBottom>Açı: {angle}°</Typography>
                  <Slider
                    value={angle}
                    onChange={handleAngleChange}
                    min={0}
                    max={360}
                    valueLabelDisplay="auto"
                  />
                </Box>
              </Box>

              {/* Dışa Aktarma Butonları */}
              <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" gap={1}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Download />}
                  onClick={downloadSVG}
                >
                  SVG
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Code />}
                  onClick={() => handleCopyText(getSVGCode(), 'svg')}
                  color={copied === 'svg' ? 'success' : 'primary'}
                >
                  {copied === 'svg' ? 'Kopyalandı!' : 'SVG Kodu'}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Code />}
                  onClick={() => handleCopyText(getCanvasCode(), 'canvas')}
                  color={copied === 'canvas' ? 'success' : 'primary'}
                >
                  {copied === 'canvas' ? 'Kopyalandı!' : 'Canvas'}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Animation />}
                  onClick={() => handleCopyText(getAnimatedCSS(), 'anim')}
                  color={copied === 'anim' ? 'success' : 'primary'}
                >
                  {copied === 'anim' ? 'Kopyalandı!' : 'Animasyon CSS'}
                </Button>
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2 }}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Dil Seçin</InputLabel>
                <Select
                  value={selectedLang}
                  label="Dil Seçin"
                  onChange={handleLangChange}
                >
                  {gradientLanguages.map(lang => (
                    <MenuItem key={lang.name} value={lang.name}>{lang.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {codes.map((item, idx) => (
                <Box key={item.type} sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ color: langColor, mb: 1 }}>
                    {item.type} Gradient
                  </Typography>
                  <Box
                    sx={{
                      fontFamily: 'monospace',
                      p: 1,
                      bgcolor: 'grey.100',
                      borderRadius: 1,
                      color: langColor,
                      wordBreak: 'break-all',
                    }}
                  >
                    {item.code}
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                    <Button
                      startIcon={<ContentCopy />}
                      onClick={() => handleCopy(item.code, idx)}
                      sx={{ bgcolor: langColor, color: '#fff', fontWeight: 700 }}
                    >
                      {copiedIndex === idx ? 'Kopyalandı!' : 'Kopyala'}
                    </Button>
                  </Box>
                </Box>
              ))}
            </Paper>
          </Grid>
          <Grid item xs={false} md={4}></Grid>
          <Grid item xs={12} md={4}>

          </Grid>
          <Grid item xs={false} md={4}></Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default GradientCreator; 